import { useState, useEffect, useCallback } from "react";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";

/**
 * Currency input that displays values with thousand separators
 * Uses Dutch format: 10.000,00
 */
const CurrencyInput = ({ 
  value, 
  onChange, 
  className,
  placeholder = "0",
  disabled = false,
  ...props 
}) => {
  const [displayValue, setDisplayValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  // Format number with thousand separators (Dutch format: 1.234.567,89)
  const formatWithThousands = useCallback((num) => {
    if (num === null || num === undefined || num === "") return "";
    
    const number = typeof num === "string" ? parseFloat(num) : num;
    if (isNaN(number)) return "";
    
    // Format with Dutch locale (dots for thousands, comma for decimals)
    return number.toLocaleString("nl-NL", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }, []);

  // Parse formatted string back to number
  const parseFormattedValue = useCallback((str) => {
    if (!str || str === "" || str === "-") return str === "-" ? "-" : "";
    
    // Remove thousand separators (dots) and replace comma with dot for parsing
    const cleaned = str
      .replace(/\./g, "")  // Remove thousand separators
      .replace(",", ".");   // Replace decimal comma with dot
    
    const num = parseFloat(cleaned);
    return isNaN(num) ? "" : num;
  }, []);

  // Update display value when external value changes
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatWithThousands(value));
    }
  }, [value, isFocused, formatWithThousands]);

  const handleFocus = () => {
    setIsFocused(true);
    // Show raw number on focus for easier editing
    if (value !== null && value !== undefined && value !== "") {
      // Show with comma as decimal separator but without thousand separators
      const num = typeof value === "string" ? parseFloat(value) : value;
      if (!isNaN(num)) {
        setDisplayValue(num.toString().replace(".", ","));
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Format with thousand separators on blur
    const numValue = parseFormattedValue(displayValue);
    if (numValue !== "" && numValue !== "-") {
      setDisplayValue(formatWithThousands(numValue));
      onChange(numValue);
    } else {
      setDisplayValue("");
      onChange(0);
    }
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Allow digits, comma (decimal), dot (thousand sep), and minus
    const sanitized = inputValue.replace(/[^0-9.,-]/g, "");
    setDisplayValue(sanitized);
    
    // Parse and update parent immediately for real-time sync
    const numValue = parseFormattedValue(sanitized);
    if (numValue !== "" && !isNaN(numValue)) {
      onChange(numValue);
    }
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      className={cn("text-right font-mono", className)}
      {...props}
    />
  );
};

export default CurrencyInput;
